import { System } from '@ecs/System';
import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { Renderable, ShipShape } from '@components/Renderable';
import { Velocity } from '@components/Velocity';
import { BulletTrail } from '@components/BulletTrail';
import { TargetHighlight } from '@components/TargetHighlight';
import { WeaponRangeDisplay } from '@components/WeaponRangeDisplay';
import { HealthDisplay } from '@components/HealthDisplay';
import { ShieldDisplay } from '@components/ShieldDisplay';
import { PlayerInput } from '@components/PlayerInput';
import { HitFlash } from '@components/HitFlash';
import { AimTelegraph } from '@components/AimTelegraph';
import { Health } from '@components/Health';
import { CameraShake } from '@components/CameraShake';
import { Selectable } from '@components/Selectable';
import { SelectionBox } from '@components/SelectionBox';
import { ShieldProjector } from '@components/ShieldProjector';
import { getShipGeometry, isShipShape, Point } from '@renderSystems/ShipShapes';
import { generateStarfield, generateNebulae, Star, Nebula } from '@renderSystems/Starfield';
import { getCameraOffset } from '@utils/CameraUtils';
import { shadeColor, tintColor, withAlpha } from '@utils/ColorUtils';

const BACKGROUND_COLOR = '#02030a';
const STAR_DRIFT = [0.004, 0.009, 0.018]; // px per ms for far, mid and near layers

/** Extra drawing hooked into the frame, e.g. the base screen's berths and labels. */
export type RenderLayer = (ctx: CanvasRenderingContext2D, time: number) => void;

interface RenderItem {
  entity: Entity;
  position: Position;
  renderable: Renderable;
  velocity?: Velocity;
}

export class PureRenderSystem extends System {
  private ctx!: CanvasRenderingContext2D;
  private readonly stars: Star[] = generateStarfield(1337, 280);
  private readonly nebulae: Nebula[] = generateNebulae(4242, 5);
  private backdrop: HTMLCanvasElement | null = null;
  // Animation clock in ms since the renderer was created. Raw Date.now() values are
  // too large for canvas angle maths and make arcs collapse.
  private readonly epoch = Date.now();
  private time = 0;
  private underlay: RenderLayer | null = null;
  private overlay: RenderLayer | null = null;

  setContext(ctx: CanvasRenderingContext2D): void {
    this.ctx = ctx;
  }

  /** Drawn after the backdrop, beneath every entity. */
  setUnderlay(layer: RenderLayer | null): void {
    this.underlay = layer;
  }

  /** Drawn on top of every entity and its overlays. */
  setOverlay(layer: RenderLayer | null): void {
    this.overlay = layer;
  }

  update(_deltaTime: number): void {
    if (!this.ctx) return;

    const ctx = this.ctx;
    const { width, height } = ctx.canvas;
    this.time = Date.now() - this.epoch;

    this.drawBackground(width, height);

    // Sort entities into draw layers
    const under: RenderItem[] = [];
    const projectiles: RenderItem[] = [];
    const ships: RenderItem[] = [];
    const particles: RenderItem[] = [];

    for (const entity of this.getEntitiesWithComponents(Position, Renderable)) {
      const item: RenderItem = {
        entity,
        position: entity.getComponent(Position)!,
        renderable: entity.getComponent(Renderable)!,
        velocity: entity.getComponent(Velocity),
      };
      const shape = item.renderable.shape;
      if (shape === 'ring' || shape === 'smoke' || shape === 'mine') under.push(item);
      else if (isShipShape(shape)) ships.push(item);
      else if (shape === 'bolt' || shape === 'circle' || shape === 'triangle') projectiles.push(item);
      else particles.push(item);
    }

    ctx.save();
    const shake = this.getEntitiesWithComponents(CameraShake)[0]?.getComponent(CameraShake);
    if (shake) {
      const offset = getCameraOffset(shake);
      ctx.translate(offset.x, offset.y);
    }

    this.underlay?.(ctx, this.time);

    // Ground-level indicators
    for (const item of ships) {
      this.renderSelection(item);
      this.renderShieldProjector(item);
      this.renderWeaponRange(item);
      this.renderAimTelegraph(item);
      this.renderMovementTarget(item);
    }

    for (const item of under) this.renderEffect(item);

    for (const item of projectiles) {
      this.renderBulletTrail(item);
      this.renderProjectile(item);
    }

    for (const item of ships) {
      this.renderBulletTrail(item);
      this.renderShip(item);
    }

    ctx.globalCompositeOperation = 'lighter';
    for (const item of particles) this.renderEffect(item);
    ctx.globalCompositeOperation = 'source-over';

    // Overlays
    for (const item of ships) {
      this.renderShield(item);
      this.renderHealthBar(item);
      this.renderTargetHighlight(item);
    }

    this.overlay?.(ctx, this.time);

    ctx.restore();

    this.renderSelectionBox();
  }

  // ---------------------------------------------------------------------------
  // Background

  private drawBackground(width: number, height: number): void {
    const ctx = this.ctx;

    if (!this.backdrop || this.backdrop.width !== width || this.backdrop.height !== height) {
      this.backdrop = this.buildBackdrop(width, height);
    }

    if (this.backdrop) {
      ctx.drawImage(this.backdrop, 0, 0);
    } else {
      ctx.fillStyle = BACKGROUND_COLOR;
      ctx.fillRect(0, 0, width, height);
    }

    for (const star of this.stars) {
      const drift = this.time * STAR_DRIFT[star.layer];
      const x = ((star.x * width - drift) % width + width) % width;
      const y = star.y * height;
      const twinkle = 0.65 + 0.35 * Math.sin(this.time * star.twinkleSpeed + star.twinklePhase);
      const alpha = star.brightness * twinkle;

      ctx.globalAlpha = alpha;
      ctx.fillStyle = star.color;
      ctx.fillRect(x - star.size / 2, y - star.size / 2, star.size, star.size);

      // Bright near stars get a small cross glint
      if (star.layer === 2 && alpha > 0.7) {
        ctx.globalAlpha = alpha * 0.35;
        ctx.fillRect(x - star.size * 2, y - 0.25, star.size * 4, 0.5);
        ctx.fillRect(x - 0.25, y - star.size * 2, 0.5, star.size * 4);
      }
    }
    ctx.globalAlpha = 1;
  }

  /** Nebulae and vignette are static, so they are painted once per canvas size. */
  private buildBackdrop(width: number, height: number): HTMLCanvasElement | null {
    if (typeof document === 'undefined' || width === 0 || height === 0) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.fillStyle = BACKGROUND_COLOR;
    ctx.fillRect(0, 0, width, height);

    const extent = Math.max(width, height);
    for (const nebula of this.nebulae) {
      const x = nebula.x * width;
      const y = nebula.y * height;
      const radius = nebula.radius * extent;
      const [r, g, b] = nebula.color;
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${nebula.alpha})`);
      gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${nebula.alpha * 0.4})`);
      gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }

    const vignette = ctx.createRadialGradient(
      width / 2, height / 2, extent * 0.3, width / 2, height / 2, extent * 0.75
    );
    vignette.addColorStop(0, 'rgba(0, 0, 0, 0)');
    vignette.addColorStop(1, 'rgba(0, 0, 0, 0.6)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, width, height);

    return canvas;
  }

  // ---------------------------------------------------------------------------
  // Ships

  private renderShip({ entity, position, renderable, velocity }: RenderItem): void {
    const ctx = this.ctx;
    const geometry = getShipGeometry(renderable.shape as ShipShape);
    const size = renderable.size;
    const color = renderable.color;

    ctx.save();
    ctx.globalAlpha = renderable.alpha;
    ctx.translate(position.x, position.y);
    ctx.rotate(velocity?.angle ?? 0);

    this.renderEngineFlames(geometry.engines, geometry.engineColor, size, this.throttle(velocity));

    // Hull with a nose-to-tail light falloff
    this.traceHull(geometry.hull, size);
    const hullGradient = ctx.createLinearGradient(size * 1.2, 0, -size, 0);
    hullGradient.addColorStop(0, tintColor(color, 0.35));
    hullGradient.addColorStop(0.45, color);
    hullGradient.addColorStop(1, shadeColor(color, 0.4));
    ctx.fillStyle = hullGradient;
    ctx.shadowColor = renderable.shadowColor ?? color;
    ctx.shadowBlur = Math.max(8, renderable.shadowBlur);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = renderable.strokeColor ?? tintColor(color, 0.5);
    ctx.lineWidth = size > 15 ? 1.5 : 1;
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Panel lines
    if (geometry.panels.length > 0) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      for (const line of geometry.panels) {
        ctx.moveTo(line[0].x * size, line[0].y * size);
        for (const point of line.slice(1)) ctx.lineTo(point.x * size, point.y * size);
      }
      ctx.stroke();
    }

    if (geometry.hangars) this.renderHangars(geometry.hangars, size, color);

    // Cockpit canopy
    if (geometry.cockpit) {
      const c = geometry.cockpit;
      const canopy = ctx.createRadialGradient(
        (c.x + c.rx * 0.3) * size, -c.ry * 0.4 * size, 0,
        c.x * size, c.y * size, Math.max(c.rx, c.ry) * size
      );
      canopy.addColorStop(0, '#ffffff');
      canopy.addColorStop(0.4, c.color);
      canopy.addColorStop(1, shadeColor(c.color, 0.35));
      ctx.fillStyle = canopy;
      ctx.beginPath();
      ctx.ellipse(c.x * size, c.y * size, c.rx * size, c.ry * size, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Blinking running lights
    const blinkOn = Math.floor((this.time + entity.id * 137) / 450) % 2 === 0;
    if ((blinkOn || geometry.lightChase) && geometry.lights.length > 0) {
      const lightRadius = Math.max(0.8, size * (geometry.lightRadius ?? 0.06));
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = geometry.engineColor;
      ctx.shadowBlur = 6;
      geometry.lights.forEach((light, i) => {
        const n = geometry.lights.length;
        if (geometry.lightChase && ((Math.floor(this.time / 140) - i) % n + n) % n > 2) return;
        ctx.beginPath();
        ctx.arc(light.x * size, light.y * size, lightRadius, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.shadowBlur = 0;
    }

    // Damage flash
    const flash = entity.getComponent(HitFlash);
    if (flash) {
      this.traceHull(geometry.hull, size);
      ctx.fillStyle = `rgba(255, 255, 255, ${0.85 * flash.frames / flash.maxFrames})`;
      ctx.fill();
    }

    ctx.restore();
  }

  /** Recessed hangar bays with glowing edges and guide lights running towards the doors. */
  private renderHangars(hangars: NonNullable<ReturnType<typeof getShipGeometry>['hangars']>, size: number, color: string): void {
    const ctx = this.ctx;
    for (const [index, bay] of hangars.entries()) {
      const x = (bay.x - bay.w / 2) * size;
      const y = (bay.y - bay.h / 2) * size;
      const w = bay.w * size;
      const h = bay.h * size;

      // Deep interior, lit from within
      const interior = ctx.createLinearGradient(0, y, 0, y + h);
      interior.addColorStop(0, '#05070d');
      interior.addColorStop(0.5, shadeColor(color, 0.22));
      interior.addColorStop(1, '#05070d');
      ctx.fillStyle = interior;
      ctx.fillRect(x, y, w, h);

      ctx.strokeStyle = withAlpha(tintColor(color, 0.4), 0.8);
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, w, h);

      // Deck guide lights chasing towards the bow
      const count = Math.max(4, Math.round(w / 14));
      const lit = Math.floor(this.time / 90 + index * 3) % count;
      for (let i = 0; i < count; i++) {
        const glow = i === lit ? 1 : i === (lit + count - 1) % count ? 0.5 : 0.15;
        ctx.fillStyle = `rgba(120, 200, 255, ${glow})`;
        const lx = x + ((i + 0.5) / count) * w;
        ctx.fillRect(lx - 1, y + 1, 2, 2);
        ctx.fillRect(lx - 1, y + h - 3, 2, 2);
      }

      // Deck markings on the main flight deck
      if (h > size * 0.2) {
        ctx.strokeStyle = 'rgba(160, 200, 255, 0.18)';
        ctx.setLineDash([6, 6]);
        ctx.beginPath();
        ctx.moveTo(x + 4, 0);
        ctx.lineTo(x + w - 4, 0);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
  }

  private traceHull(hull: Point[], size: number): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(hull[0].x * size, hull[0].y * size);
    for (const point of hull.slice(1)) ctx.lineTo(point.x * size, point.y * size);
    ctx.closePath();
  }

  private throttle(velocity?: Velocity): number {
    if (!velocity || velocity.speed <= 0.01) return 0;
    if (!Number.isFinite(velocity.maxSpeed) || velocity.maxSpeed <= 0) return 1;
    return Math.min(1, velocity.speed / velocity.maxSpeed);
  }

  private renderEngineFlames(engines: Point[], color: string, size: number, throttle: number): void {
    const ctx = this.ctx;
    const width = size * (engines.length > 2 ? 0.1 : 0.14);

    ctx.globalCompositeOperation = 'lighter';
    for (const engine of engines) {
      const x = engine.x * size;
      const y = engine.y * size;
      const flicker = 0.8 + Math.random() * 0.4;
      const length = size * (0.25 + 1.1 * throttle) * flicker;

      const flame = ctx.createLinearGradient(x, y, x - length, y);
      flame.addColorStop(0, withAlpha(color, 0.95));
      flame.addColorStop(1, withAlpha(color, 0));
      ctx.fillStyle = flame;
      ctx.beginPath();
      ctx.moveTo(x, y - width);
      ctx.lineTo(x - length, y);
      ctx.lineTo(x, y + width);
      ctx.closePath();
      ctx.fill();

      // White-hot nozzle core
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.beginPath();
      ctx.arc(x, y, width * 0.7, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  // ---------------------------------------------------------------------------
  // Projectiles

  private renderProjectile({ renderable, position, velocity }: RenderItem): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = renderable.alpha;

    if (renderable.shape === 'bolt') {
      const angle = velocity?.angle ?? 0;
      const length = renderable.size * 3 + (velocity?.speed ?? 0) * 1.2;
      const tailX = position.x - Math.cos(angle) * length;
      const tailY = position.y - Math.sin(angle) * length;

      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';
      ctx.strokeStyle = withAlpha(renderable.color, 0.45);
      ctx.lineWidth = renderable.size * 2.4;
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(position.x, position.y);
      ctx.stroke();

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = Math.max(1, renderable.size * 0.9);
      ctx.beginPath();
      ctx.moveTo(position.x - Math.cos(angle) * length * 0.6, position.y - Math.sin(angle) * length * 0.6);
      ctx.lineTo(position.x, position.y);
      ctx.stroke();
    } else if (renderable.shape === 'triangle') {
      ctx.translate(position.x, position.y);
      ctx.rotate(velocity?.angle ?? 0);
      ctx.fillStyle = renderable.color;
      ctx.beginPath();
      ctx.moveTo(renderable.size, 0);
      ctx.lineTo(-renderable.size * 0.7, -renderable.size * 0.7);
      ctx.lineTo(-renderable.size * 0.7, renderable.size * 0.7);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.fillStyle = renderable.color;
      if (renderable.shadowColor && renderable.shadowBlur > 0) {
        ctx.shadowColor = renderable.shadowColor;
        ctx.shadowBlur = renderable.shadowBlur;
      }
      ctx.beginPath();
      ctx.arc(position.x, position.y, renderable.size, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private renderBulletTrail({ entity, position, renderable, velocity }: RenderItem): void {
    const trail = entity.getComponent(BulletTrail);
    if (!trail || !velocity) return;

    const ctx = this.ctx;
    const length = trail.length * Math.min(3, Math.max(0.6, velocity.speed / 6));
    const tailX = position.x - Math.cos(velocity.angle) * length;
    const tailY = position.y - Math.sin(velocity.angle) * length;

    const gradient = ctx.createLinearGradient(position.x, position.y, tailX, tailY);
    gradient.addColorStop(0, withAlpha(renderable.color, 0.6));
    gradient.addColorStop(1, withAlpha(renderable.color, 0));

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = gradient;
    ctx.lineWidth = Math.max(1, renderable.size * 0.8);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(position.x, position.y);
    ctx.lineTo(tailX, tailY);
    ctx.stroke();
    ctx.restore();
  }

  // ---------------------------------------------------------------------------
  // Particles and effects

  private renderEffect({ entity, position, renderable, velocity }: RenderItem): void {
    const ctx = this.ctx;
    const alpha = renderable.alpha;
    if (alpha <= 0) return;

    switch (renderable.shape) {
      case 'particle': {
        const radius = renderable.size * (0.4 + 0.6 * alpha);
        ctx.globalAlpha = alpha * 0.25;
        ctx.fillStyle = renderable.color;
        ctx.beginPath();
        ctx.arc(position.x, position.y, radius * 2.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(position.x, position.y, radius, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 'spark': {
        const angle = velocity?.angle ?? 0;
        const length = 2 + (velocity?.speed ?? 0) * 2.5;
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = renderable.color;
        ctx.lineWidth = renderable.size;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(position.x, position.y);
        ctx.lineTo(position.x - Math.cos(angle) * length, position.y - Math.sin(angle) * length);
        ctx.stroke();
        break;
      }
      case 'ring': {
        ctx.globalAlpha = alpha * 0.8;
        ctx.strokeStyle = renderable.color;
        ctx.lineWidth = 0.5 + 3 * alpha;
        ctx.beginPath();
        ctx.arc(position.x, position.y, renderable.size, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case 'smoke': {
        ctx.globalAlpha = alpha * 0.3;
        ctx.fillStyle = renderable.color;
        ctx.beginPath();
        ctx.arc(position.x, position.y, renderable.size, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 'mine':
        this.renderMine(entity, position, renderable);
        break;
    }
    ctx.globalAlpha = 1;
  }

  private renderMine(entity: Entity, position: Position, renderable: Renderable): void {
    const ctx = this.ctx;
    const size = renderable.size;
    const pulse = 0.5 + 0.5 * Math.sin(this.time * 0.008 + entity.id);
    const spin = this.time * 0.0015 + entity.id;

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.translate(position.x, position.y);

    // Proximity danger halo
    ctx.fillStyle = withAlpha(renderable.color, 0.06 + 0.08 * pulse);
    ctx.beginPath();
    ctx.arc(0, 0, size * 2.2, 0, Math.PI * 2);
    ctx.fill();

    ctx.rotate(spin);
    ctx.strokeStyle = renderable.color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI * 2 * i) / 6;
      ctx.moveTo(Math.cos(a) * size * 0.8, Math.sin(a) * size * 0.8);
      ctx.lineTo(Math.cos(a) * size * 1.45, Math.sin(a) * size * 1.45);
    }
    ctx.stroke();

    ctx.fillStyle = shadeColor(renderable.color, 0.45);
    ctx.beginPath();
    ctx.arc(0, 0, size, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = `rgba(255, 60, 40, ${0.4 + 0.6 * pulse})`;
    ctx.shadowColor = '#ff3322';
    ctx.shadowBlur = 8 * pulse;
    ctx.beginPath();
    ctx.arc(0, 0, size * 0.35, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // ---------------------------------------------------------------------------
  // Overlays

  private renderTargetHighlight({ entity, position, renderable }: RenderItem): void {
    const highlight = entity.getComponent(TargetHighlight);
    if (!highlight || !highlight.isTargeted) return;

    const ctx = this.ctx;
    const radius = renderable.size + highlight.highlightRadius;
    const bracket = Math.max(4, radius * 0.4);
    const spin = this.time * 0.002;

    ctx.save();
    ctx.translate(position.x, position.y);
    ctx.rotate(spin);
    ctx.strokeStyle = highlight.highlightColor;
    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.7 + 0.3 * Math.sin(this.time * 0.01);
    for (let i = 0; i < 4; i++) {
      ctx.rotate(Math.PI / 2);
      ctx.beginPath();
      ctx.moveTo(radius - bracket, -radius);
      ctx.lineTo(radius, -radius);
      ctx.lineTo(radius, -radius + bracket);
      ctx.stroke();
    }
    ctx.restore();
  }

  private renderWeaponRange({ entity, position }: RenderItem): void {
    const rangeDisplay = entity.getComponent(WeaponRangeDisplay);
    if (!rangeDisplay || !rangeDisplay.showRange) return;

    const ctx = this.ctx;
    ctx.save();
    ctx.fillStyle = rangeDisplay.rangeColor;
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    ctx.arc(position.x, position.y, rangeDisplay.range, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 1;
    ctx.strokeStyle = rangeDisplay.rangeStrokeColor;
    ctx.lineWidth = 1;
    if (rangeDisplay.rangeStyle === 'dashed') {
      ctx.setLineDash([5, 10]);
      ctx.lineDashOffset = -this.time * 0.01;
    }
    ctx.stroke();
    ctx.restore();
  }

  private renderAimTelegraph({ entity, position }: RenderItem): void {
    const telegraph = entity.getComponent(AimTelegraph);
    if (!telegraph || !telegraph.active) return;

    const ctx = this.ctx;
    const endX = position.x + Math.cos(telegraph.angle) * telegraph.length;
    const endY = position.y + Math.sin(telegraph.angle) * telegraph.length;
    const imminent = telegraph.progress > 0.75;

    ctx.save();
    ctx.strokeStyle = telegraph.color;
    ctx.globalAlpha = imminent ? 0.5 + 0.5 * Math.sin(this.time * 0.05) : 0.15 + 0.4 * telegraph.progress;
    ctx.lineWidth = 0.5 + 1.5 * telegraph.progress;
    if (!imminent) ctx.setLineDash([8, 6]);
    ctx.beginPath();
    ctx.moveTo(position.x, position.y);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    // Crosshair where the shot will land
    ctx.setLineDash([]);
    const r = 6 + 6 * (1 - telegraph.progress);
    ctx.beginPath();
    ctx.arc(endX, endY, r, 0, Math.PI * 2);
    ctx.moveTo(endX - r * 1.5, endY);
    ctx.lineTo(endX + r * 1.5, endY);
    ctx.moveTo(endX, endY - r * 1.5);
    ctx.lineTo(endX, endY + r * 1.5);
    ctx.stroke();
    ctx.restore();
  }

  private renderHealthBar({ entity, position, renderable }: RenderItem): void {
    const healthDisplay = entity.getComponent(HealthDisplay);
    if (!healthDisplay || !healthDisplay.showHealthBar) return;

    const ctx = this.ctx;
    const width = Math.max(healthDisplay.barWidth, renderable.size * 2.4);
    const height = healthDisplay.barHeight;
    const x = position.x - width / 2;
    const y = position.y - Math.max(healthDisplay.barOffset, renderable.size * 1.6);
    const segments = Math.min(entity.getComponent(Health)?.max ?? 1, 12);

    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(x - 1, y - 1, width + 2, height + 2);

    ctx.fillStyle = healthDisplay.barColor;
    ctx.shadowColor = healthDisplay.barColor;
    ctx.shadowBlur = 4;
    ctx.fillRect(x, y, width * healthDisplay.healthPercentage, height);
    ctx.shadowBlur = 0;

    // Segment dividers, one per hit point
    if (segments > 1) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      for (let i = 1; i < segments; i++) {
        ctx.fillRect(x + (width * i) / segments - 0.5, y, 1, height);
      }
    }
    ctx.restore();
  }

  private renderShield({ entity, position }: RenderItem): void {
    const shieldDisplay = entity.getComponent(ShieldDisplay);
    if (!shieldDisplay || !shieldDisplay.showShield) return;

    const ctx = this.ctx;
    const opacity = Math.max(0, Math.min(1, shieldDisplay.shieldOpacity + shieldDisplay.pulseIntensity));
    const radius = shieldDisplay.shieldRadius;
    const color = shieldDisplay.shieldColor;

    ctx.save();
    const bubble = ctx.createRadialGradient(position.x, position.y, radius * 0.55, position.x, position.y, radius);
    bubble.addColorStop(0, withAlpha(color, 0));
    bubble.addColorStop(0.85, withAlpha(color, opacity * 0.25));
    bubble.addColorStop(1, withAlpha(color, opacity * 0.5));
    ctx.fillStyle = bubble;
    ctx.beginPath();
    ctx.arc(position.x, position.y, radius, 0, Math.PI * 2);
    ctx.fill();

    // Rim with a slowly travelling highlight
    ctx.strokeStyle = withAlpha(color, opacity);
    ctx.lineWidth = 1.5;
    ctx.stroke();
    const sweep = this.time * 0.003 + entity.id;
    ctx.strokeStyle = withAlpha('#ffffff', opacity * 0.6);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(position.x, position.y, radius, sweep, sweep + 0.8);
    ctx.stroke();
    ctx.restore();
  }

  private renderSelection({ entity, position, renderable }: RenderItem): void {
    if (!entity.getComponent(Selectable)?.selected) return;

    const ctx = this.ctx;
    const shieldRadius = entity.getComponent(ShieldDisplay)?.shieldRadius ?? 0;
    const radius = Math.max(renderable.size * 1.7, shieldRadius) + 6;
    const spin = this.time * 0.0015;
    ctx.save();
    ctx.strokeStyle = 'rgba(80, 255, 160, 0.95)';
    ctx.lineWidth = 2;

    // Four bracket arcs rotating slowly around the ship
    for (let i = 0; i < 4; i++) {
      const start = spin + (Math.PI / 2) * i;
      ctx.beginPath();
      ctx.arc(position.x, position.y, radius, start + 0.2, start + Math.PI / 2 - 0.2);
      ctx.stroke();
    }

    // Focus-fire link to the ordered target
    const input = entity.getComponent(PlayerInput);
    const target = input?.targetEntity != null ? this.world.getEntity(input.targetEntity)?.getComponent(Position) : undefined;
    if (target) {
      ctx.strokeStyle = 'rgba(255, 90, 90, 0.45)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 5]);
      ctx.beginPath();
      ctx.moveTo(position.x, position.y);
      ctx.lineTo(target.x, target.y);
      ctx.stroke();
    }
    ctx.restore();
  }

  private renderShieldProjector({ entity, position }: RenderItem): void {
    const projector = entity.getComponent(ShieldProjector);
    if (!projector) return;

    const ctx = this.ctx;
    const pulse = 0.5 + 0.5 * Math.sin(this.time * 0.003 + entity.id);
    ctx.save();
    ctx.strokeStyle = `rgba(80, 240, 224, ${0.12 + 0.12 * pulse})`;
    ctx.fillStyle = `rgba(80, 240, 224, ${0.025 + 0.02 * pulse})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(position.x, position.y, projector.range, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  private renderSelectionBox(): void {
    const box = this.getEntitiesWithComponents(SelectionBox)[0]?.getComponent(SelectionBox);
    if (!box?.active) return;

    const ctx = this.ctx;
    const x = Math.min(box.x1, box.x2);
    const y = Math.min(box.y1, box.y2);
    const width = Math.abs(box.x2 - box.x1);
    const height = Math.abs(box.y2 - box.y1);
    ctx.save();
    ctx.fillStyle = 'rgba(80, 255, 160, 0.08)';
    ctx.strokeStyle = 'rgba(80, 255, 160, 0.8)';
    ctx.lineWidth = 1;
    ctx.fillRect(x, y, width, height);
    ctx.strokeRect(x + 0.5, y + 0.5, width, height);
    ctx.restore();
  }

  private renderMovementTarget({ entity, position }: RenderItem): void {
    const playerInput = entity.getComponent(PlayerInput);
    if (!playerInput?.targetPosition) return;
    const selected = entity.getComponent(Selectable)?.selected ?? true;

    const ctx = this.ctx;
    const target = playerInput.targetPosition;
    const pulse = (this.time % 1000) / 1000;

    ctx.save();
    ctx.globalAlpha = selected ? 1 : 0.35;
    ctx.strokeStyle = 'rgba(0, 255, 136, 0.25)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 6]);
    ctx.lineDashOffset = -this.time * 0.02;
    ctx.beginPath();
    ctx.moveTo(position.x, position.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(target.x, target.y, 6, 0, Math.PI * 2);
    ctx.stroke();

    ctx.globalAlpha = (1 - pulse) * (selected ? 1 : 0.35);
    ctx.beginPath();
    ctx.arc(target.x, target.y, 6 + pulse * 12, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
}
